/**
 * Affiliate Dashboard
 * Main dashboard for affiliate portal
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { affiliateApi } from '../../api/affiliate';
import StatisticsCards from '../../components/affiliate/StatisticsCards';
import PerformanceChart from '../../components/affiliate/PerformanceChart';
import RecentReferrals from '../../components/affiliate/RecentReferrals';
import ReferralLinkGenerator from '../../components/affiliate/ReferralLinkGenerator';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import ErrorMessage from '../../components/ui/ErrorMessage';

interface DashboardData {
  affiliate: {
    id: number;
    firstName: string;
    lastName: string;
    affiliateCode: string;
    status: string;
    tier: {
      id: number;
      name: string;
      commissionRate: number;
      commissionType: string;
    } | null;
  };
  statistics: {
    totalClicks: number;
    totalReferrals: number;
    totalConversions: number;
    conversionRate: number;
    totalEarnings: number;
    pendingEarnings: number;
    paidEarnings: number;
  };
  commissionStats: any;
  recentReferrals: any[];
  recentCommissions: any[];
}

export default function AffiliateDashboard() {
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await affiliateApi.getDashboard();
      setDashboard(response.dashboard);
    } catch (err: any) {
      setError(err.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error} onRetry={loadDashboard} />
      </div>
    );
  }

  if (!dashboard) {
    return null;
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">
            Welcome back, {dashboard.affiliate.firstName}!
          </h1>
          <p className="text-slate-400 mt-1">
            Affiliate Code:{' '}
            <span className="font-mono text-orange-400">
              {dashboard.affiliate.affiliateCode}
            </span>
          </p>
          {dashboard.affiliate.tier && (
            <p className="text-slate-400">
              Tier: <span className="text-purple-400">{dashboard.affiliate.tier.name}</span>{' '}
              ({dashboard.affiliate.tier.commissionRate}%{' '}
              {dashboard.affiliate.tier.commissionType})
            </p>
          )}
        </div>

        <div className="flex space-x-3">
          <Link
            to="/affiliate/materials"
            className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition"
          >
            Marketing Materials
          </Link>
          <Link
            to="/affiliate/profile"
            className="px-4 py-2 bg-slate-700/50 text-white rounded-lg hover:bg-slate-600/50 transition"
          >
            Profile Settings
          </Link>
        </div>
      </div>

      {/* Status Alert */}
      {dashboard.affiliate.status !== 'active' && (
        <div className="bg-yellow-900/20 border border-yellow-600 rounded-lg p-4">
          <div className="flex items-center">
            <svg
              className="w-5 h-5 text-yellow-500 mr-3"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <p className="text-yellow-500 font-medium">Account Status: {dashboard.affiliate.status}</p>
              {dashboard.affiliate.status === 'pending' && (
                <p className="text-sm text-slate-400 mt-1">
                  Your affiliate account is pending approval. You'll be notified once it's activated.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Statistics Cards */}
      <StatisticsCards statistics={dashboard.statistics} />

      {/* Referral Link Generator */}
      <ReferralLinkGenerator affiliateCode={dashboard.affiliate.affiliateCode} />

      {/* Performance Chart */}
      <PerformanceChart />

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Referrals */}
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-white">Recent Referrals</h2>
            <Link
              to="/affiliate/referrals"
              className="text-sm text-orange-400 hover:text-orange-300"
            >
              View All
            </Link>
          </div>
          <RecentReferrals referrals={dashboard.recentReferrals} />
        </div>

        {/* Recent Commissions */}
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-white">Recent Commissions</h2>
            <Link
              to="/affiliate/commissions"
              className="text-sm text-orange-400 hover:text-orange-300"
            >
              View All
            </Link>
          </div>
          {dashboard.recentCommissions.length === 0 ? (
            <p className="text-slate-400 text-center py-8">No commissions yet</p>
          ) : (
            <div className="space-y-3">
              {dashboard.recentCommissions.map((commission: any) => (
                <div
                  key={commission.id}
                  className="flex items-center justify-between p-3 bg-slate-700/50/50 rounded-lg"
                >
                  <div className="flex-1">
                    <p className="text-white font-medium">
                      ${commission.commissionAmount.toFixed(2)}
                    </p>
                    <p className="text-sm text-slate-400">
                      Order #{commission.orderId}
                    </p>
                  </div>
                  <div className="text-right">
                    <span
                      className={`px-2 py-1 text-xs rounded-full ${
                        commission.status === 'approved'
                          ? 'bg-green-900/30 text-green-400'
                          : commission.status === 'pending'
                          ? 'bg-yellow-900/30 text-yellow-400'
                          : commission.status === 'paid'
                          ? 'bg-blue-900/30 text-orange-400'
                          : 'bg-slate-700/50 text-slate-400'
                      }`}
                    >
                      {commission.status}
                    </span>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(commission.earnedAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link
          to="/affiliate/referrals"
          className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg p-6 hover:from-blue-700 hover:to-blue-800 transition group"
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-white font-semibold mb-1">View Referrals</h3>
              <p className="text-blue-200 text-sm">Track your referrals</p>
            </div>
            <svg
              className="w-8 h-8 text-blue-300 group-hover:translate-x-1 transition"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </div>
        </Link>

        <Link
          to="/affiliate/commissions"
          className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-lg p-6 hover:from-purple-700 hover:to-purple-800 transition group"
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-white font-semibold mb-1">View Commissions</h3>
              <p className="text-purple-200 text-sm">Check your earnings</p>
            </div>
            <svg
              className="w-8 h-8 text-purple-300 group-hover:translate-x-1 transition"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </div>
        </Link>

        <Link
          to="/affiliate/payouts"
          className="bg-gradient-to-br from-green-600 to-green-700 rounded-lg p-6 hover:from-green-700 hover:to-green-800 transition group"
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-white font-semibold mb-1">Request Payout</h3>
              <p className="text-green-200 text-sm">Withdraw your earnings</p>
            </div>
            <svg
              className="w-8 h-8 text-green-300 group-hover:translate-x-1 transition"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </div>
        </Link>
      </div>
    </div>
  );
}
